// LineCurveHandleController.
//
// Draggable handles for a `curved` line's control points. Each control is an
// ABSOLUTE world point (`curveControls`), so it stays put when a single
// endpoint moves and only travels with a whole-line move. A curved line with no
// stored controls shows a single implicit handle at the midpoint.
//
//  - drag a handle          → move that control
//  - double-click the line  → add a control at the click (`line/add-curve-point`)
//  - double-click a handle  → remove that control
//
// Live preview repaints via `renderLine`; the single `SetLineCurveCommand` on
// each edit is the only undo step. Structure mirrors the other line handle
// controllers (selection-driven lifecycle, drag guard, follow-the-line resyncs).

import Konva from 'konva';
import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import type { History } from '@o2d/history';

import type { BoardState } from '../../core/state/board-state.js';
import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { createSelectionHandle, sizeSelectionHandle } from '../../core/konva/selection-handle.js';
import { isLine } from './line.types.js';
import type { LineObject } from './line.interface.js';
import { SetLineCurveCommand } from './line.commands.js';
import { wireHandleHover } from './line-handle-hover.js';
import { CURVED_STRATEGY_ID } from './strategies/curved-path-strategy.js';
import {
  effectiveCurveControls,
  insertCurveControl,
  removeCurveControl,
  type Pt,
} from './curve-controls.js';

const CURVE_HANDLE_NAME = 'line-curve-handle';
/** Below this pointer travel (screen px) a press is a click, not a bend. */
const MIN_BEND_SCREEN_PX = 3;

export interface LineCurveHandleDeps {
  overlayLayer(): Konva.Layer | undefined;
  selectionLayer(): Konva.Layer | undefined;
  konvaNodeFor(id: string): Konva.Node | undefined;
  getReadonly(): boolean;
  renderLine(line: LineObject): void;
}

export class LineCurveHandleController implements ReactiveController {
  readonly #bus: WhiteboardBus;
  readonly #state: BoardState;
  readonly #history: History<BoardState>;
  readonly #deps: LineCurveHandleDeps;
  readonly #disconnect$ = new Subject<void>();
  #handles: Konva.Rect[] = [];
  #selectedLineId: string | null = null;
  #dragging = false;
  #fromControls: readonly Pt[] | undefined = undefined;
  #handleStart: Pt | null = null;
  #stateTeardown: Array<() => void> = [];

  constructor(
    host: ReactiveControllerHost,
    bus: WhiteboardBus,
    state: BoardState,
    history: History<BoardState>,
    deps: LineCurveHandleDeps,
  ) {
    this.#bus = bus;
    this.#state = state;
    this.#history = history;
    this.#deps = deps;
    host.addController(this);
  }

  hostConnected(): void {
    this.#bus.selection$
      .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
      .subscribe({ next: (ids) => this.#onSelectionChanged(ids) });

    this.#bus.liveTransform$
      .pipe(takeUntil(this.#disconnect$), takeUntil(this.#bus.destroy$))
      .subscribe({ next: () => this.#refresh('live') });

    this.#bus.uiActions$
      .pipe(
        takeUntil(this.#disconnect$),
        takeUntil(this.#bus.destroy$),
        filter((a): a is Extract<UiAction, { type: 'line/add-curve-point' }> => a.type === 'line/add-curve-point'),
      )
      .subscribe({ next: (a) => this.#addPoint(a.id, a.at) });

    this.#stateTeardown = [
      this.#state.on('object-updated', ({ object }) => {
        if (object.id === this.#selectedLineId) this.#refresh('state');
      }),
      this.#state.on('viewport-changed', () => this.#refresh('state')),
    ];
  }

  hostDisconnected(): void {
    this.#selectedLineId = null;
    this.#destroyHandles();
    for (const off of this.#stateTeardown.splice(0)) off();
    this.#disconnect$.next();
    this.#disconnect$.complete();
  }

  #layer(): Konva.Layer | undefined {
    return this.#deps.overlayLayer() ?? this.#deps.selectionLayer();
  }

  #isCurved(obj: LineObject): boolean {
    return obj.pathStrategy === CURVED_STRATEGY_ID;
  }

  /** World positions of the control handles. `origin` shifts them to follow a
   * live whole-line drag (node moved, state not committed). */
  #positions(obj: LineObject, source: 'state' | 'live'): Pt[] {
    const node = source === 'live' ? this.#deps.konvaNodeFor(obj.id) : undefined;
    const dx = node ? node.x() - obj.x : 0;
    const dy = node ? node.y() - obj.y : 0;
    const start = { x: obj.x1 + dx, y: obj.y1 + dy };
    const end = { x: obj.x2 + dx, y: obj.y2 + dy };
    const shifted = obj.curveControls?.map((c) => ({ x: c.x + dx, y: c.y + dy }));
    return effectiveCurveControls(shifted, start, end);
  }

  #onSelectionChanged(ids: ReadonlyArray<string>): void {
    if (this.#dragging) return;
    let lineId: string | null = null;
    if (ids.length === 1 && !this.#deps.getReadonly()) {
      const obj = this.#state.getObject(ids[0]!);
      if (obj && isLine(obj)) lineId = ids[0]!;
    }
    if (lineId === this.#selectedLineId && this.#handles.length > 0) {
      this.#refresh('state');
      return;
    }
    this.#destroyHandles();
    this.#selectedLineId = lineId;
    this.#refresh('state');
  }

  #refresh(source: 'state' | 'live'): void {
    if (this.#dragging) return;
    if (this.#selectedLineId === null) {
      this.#destroyHandles();
      return;
    }
    const obj = this.#state.getObject(this.#selectedLineId);
    if (!obj || !isLine(obj) || !this.#isCurved(obj)) {
      this.#destroyHandles();
      return;
    }
    const positions = this.#positions(obj, source);
    // Reposition when the count matches, rebuild when a point was added/removed.
    if (this.#handles.length === positions.length) this.#repositionHandles(positions);
    else this.#buildHandles(this.#selectedLineId, positions);
  }

  #repositionHandles(positions: readonly Pt[]): void {
    const scale = this.#state.viewport.scale || 1;
    for (let i = 0; i < this.#handles.length; i++) {
      this.#handles[i]!.position(positions[i]!);
      sizeSelectionHandle(this.#handles[i]!, scale);
    }
    this.#layer()?.batchDraw();
  }

  #buildHandles(lineId: string, positions: readonly Pt[]): void {
    this.#destroyHandles();
    const layer = this.#layer();
    if (!layer) return;
    const scale = this.#state.viewport.scale || 1;
    positions.forEach((p, i) => {
      const handle = createSelectionHandle({ x: p.x, y: p.y, scale, name: CURVE_HANDLE_NAME });
      this.#wireHandle(handle, lineId, i);
      wireHandleHover(handle, () => {
        const o = this.#state.getObject(lineId);
        return o && isLine(o) ? o.strokeColor : undefined;
      });
      layer.add(handle);
      this.#handles.push(handle);
    });
    layer.batchDraw();
  }

  #wireHandle(handle: Konva.Rect, lineId: string, index: number): void {
    handle.on('click tap', (e: Konva.KonvaEventObject<MouseEvent>) => {
      e.cancelBubble = true;
    });

    // Double-click a control handle → remove that control.
    handle.on('dblclick dbltap', (e: Konva.KonvaEventObject<MouseEvent>) => {
      e.cancelBubble = true;
      this.#removePoint(lineId, index);
    });

    handle.on('dragstart', () => {
      const obj = this.#state.getObject(lineId);
      if (!obj || !isLine(obj) || !this.#isCurved(obj)) return;
      this.#dragging = true;
      this.#fromControls = obj.curveControls;
      this.#handleStart = handle.position();
    });

    handle.on('dragmove', () => {
      const obj = this.#state.getObject(lineId);
      if (!obj || !isLine(obj)) return;
      const next = this.#withControlAt(obj, index, handle.position());
      this.#deps.renderLine({ ...obj, curveControls: next });
    });

    handle.on('dragend', () => {
      const obj = this.#state.getObject(lineId);
      const from = this.#fromControls;
      const handleStart = this.#handleStart;
      this.#dragging = false;
      this.#fromControls = undefined;
      this.#handleStart = null;
      if (!obj || !isLine(obj) || !handleStart) {
        this.#refresh('state');
        return;
      }
      const scale = this.#state.viewport.scale || 1;
      const moved = Math.hypot(handle.x() - handleStart.x, handle.y() - handleStart.y) * scale;
      if (moved < MIN_BEND_SCREEN_PX) {
        this.#refresh('state');
        return;
      }
      const to = this.#withControlAt(obj, index, handle.position());
      this.#history.push(new SetLineCurveCommand(lineId, from, to), this.#state);
      this.#refresh('state');
    });
  }

  /** The control list with entry `index` set to `pt` (materialising the
   * implicit midpoint when the line had no stored controls). */
  #withControlAt(obj: LineObject, index: number, pt: Pt): Pt[] {
    const controls = effectiveCurveControls(
      obj.curveControls,
      { x: obj.x1, y: obj.y1 },
      { x: obj.x2, y: obj.y2 },
    );
    if (index >= 0 && index < controls.length) controls[index] = { x: pt.x, y: pt.y };
    return controls;
  }

  #addPoint(lineId: string, at: Pt): void {
    const obj = this.#state.getObject(lineId);
    if (!obj || !isLine(obj) || !this.#isCurved(obj)) return;
    const to = insertCurveControl(
      obj.curveControls,
      { x: obj.x1, y: obj.y1 },
      { x: obj.x2, y: obj.y2 },
      at,
    );
    this.#history.push(new SetLineCurveCommand(lineId, obj.curveControls, to), this.#state);
  }

  #removePoint(lineId: string, index: number): void {
    const obj = this.#state.getObject(lineId);
    if (!obj || !isLine(obj) || !this.#isCurved(obj)) return;
    // With no stored controls the single handle is the implicit midpoint — there
    // is nothing to delete.
    if (!obj.curveControls || index >= obj.curveControls.length) return;
    const next = removeCurveControl(obj.curveControls, index);
    this.#history.push(
      new SetLineCurveCommand(lineId, obj.curveControls, next.length > 0 ? next : undefined),
      this.#state,
    );
  }

  #destroyHandles(): void {
    this.#dragging = false;
    this.#fromControls = undefined;
    this.#handleStart = null;
    if (this.#handles.length === 0) return;
    for (const h of this.#handles) h.destroy();
    this.#handles = [];
    this.#layer()?.batchDraw();
  }
}

export const LINE_CURVE_HANDLE_NAME = CURVE_HANDLE_NAME;
